import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowRight, Sparkles, Heart, Users } from "lucide-react";
import { EVENT, STATS, FAQ } from "@/data/site";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function Home() {
  useDocumentTitle(
    null,
    "mamy!kongres – 22 maja 2027, Warszawa. Jedyny taki kongres dla mam w Polsce. 10 prelegentek, warsztaty, panel dyskusyjny, networking."
  );
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="blob bg-[#E6007A]/30 w-[900px] h-[900px] -top-40 -right-40" />
        <div className="blob bg-[#F48DBF]/70 w-[760px] h-[760px] top-32 -left-40" style={{ filter: "blur(160px)" }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-20 lg:pt-16 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-6 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E8E1D9] px-4 py-1.5 mb-8 text-xs font-medium text-[#5C534D]">
              <span className="w-2 h-2 rounded-full bg-[#E6007A]" />
              Bilety Early Bird — do wyczerpania
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-[92px] leading-[0.95] mb-8">
              Jedyny taki <span className="italic text-[#E6007A]">kongres</span><br />
              dla mam <br />w&nbsp;Polsce.
            </h1>

            <p className="text-lg text-[#5C534D] max-w-xl mb-10 leading-relaxed">
              Setki mam, które rozumieją jak to jest.<br />
              Formuła wspierająca wymianę doświadczeń i integrację.<br />
              Rozmowy, których nie doświadczysz na Instagramie.
            </p>

            <div className="flex flex-wrap items-center gap-5 mb-12">
              <div className="flex items-center gap-2 text-[#2B2623]">
                <Calendar className="w-5 h-5 text-[#E6007A]" />
                <span className="font-semibold">{EVENT.dateLong}</span>
              </div>
              <span className="w-1 h-1 rounded-full bg-[#E8E1D9]" />
              <div className="flex items-center gap-2 text-[#2B2623]">
                <MapPin className="w-5 h-5 text-[#E6007A]" />
                <span className="font-semibold">{EVENT.city}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/zapisz-sie"
                data-testid="hero-register-button"
                className="btn-lift inline-flex items-center gap-2 rounded-full bg-[#E6007A] px-8 py-4 text-white font-semibold"
              >
                Kupuję bilet
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/prelegentki"
                data-testid="hero-see-speakers"
                className="inline-flex items-center gap-2 rounded-full bg-[#2E4331] border border-[#2E4331] px-8 py-4 text-white font-semibold hover:bg-[#243528] hover:border-[#243528] transition-colors"
              >
                Poznaj prelegentki
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              <div className="relative rounded-[48px] overflow-hidden aspect-[3/4]">
                <img
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_mama-congress/artifacts/fc6eb9170179affa_Projekt%20bez%20nazwy%20%285%29.webp"
                  alt="Grupa śmiejących się mam"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-4 bg-white rounded-2xl border border-[#E8E1D9] p-5 shadow-lg z-10 max-w-[220px]">
                <div className="text-3xl font-display text-[#E6007A]">300+</div>
                <div className="text-sm text-[#5C534D]">uczestniczek.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-[#E6007A] text-white relative overflow-hidden" data-testid="stats-section">
        <div className="blob bg-white/15 w-96 h-96 -top-20 -left-20" />
        <div className="blob bg-white/10 w-80 h-80 -bottom-20 -right-20" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 relative">
          {STATS.map((s, i) => (
            <div key={i} className="text-center md:text-left">
              <div className="font-display text-5xl lg:text-6xl text-white leading-none">{s.value}</div>
              <div className="text-sm uppercase tracking-widest text-white/80 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT SHORT */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="about-teaser">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-4">O kongresie</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight mb-6">
              Miejsce, gdzie mama <br />jest <span className="italic text-[#E6007A]">sobą</span>.
            </h2>
            <p className="text-[#5C534D] text-lg leading-relaxed mb-6">
              <span className="font-display text-[#2B2623]">
                mamy<span className="text-[#E6007A]">!</span>kongres
              </span>{" "}
              to nie kolejna konferencja z patrzeniem na zegarek.<br />
              To dzień, który organizujemy tak, jak same byśmy chciały go przeżyć — z kawą w ręku,
              w wygodnym fotelu, w towarzystwie kobiet, które inspirują.<br />
              To realna wymiana zdań, kontaktów, doświadczeń oraz niezapomniane rozmowy.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[
                { icon: Sparkles, label: "Inspirujące kobiety", theme: "pink" },
                { icon: Heart, label: "Prawdziwe rozmowy", theme: "beige" },
                { icon: Users, label: "Nowe przyjaźnie", theme: "pink" },
              ].map((f, i) => {
                const isPink = f.theme === "pink";
                return (
                  <div
                    key={i}
                    className={`rounded-2xl p-5 transition-transform duration-300 hover:-translate-y-1 ${
                      isPink
                        ? "bg-[#E6007A] text-white shadow-[0_10px_25px_rgba(230,0,122,0.25)]"
                        : "bg-[#F4EFE6] text-[#2B2623]"
                    }`}
                  >
                    <f.icon className={`w-6 h-6 mb-3 ${isPink ? "text-white" : "text-[#E6007A]"}`} />
                    <div className="font-semibold">{f.label}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8">
              <Link to="/o-kongresie" className="inline-flex items-center gap-2 text-[#E6007A] font-semibold link-underline">
                Czytaj więcej <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-[40px] overflow-hidden aspect-square">
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_mama-congress/artifacts/zq4f1tr7_image.png"
                alt="Uczestniczki biją brawo"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE STRIP */}
      <section className="bg-[#E6007A] text-white overflow-hidden py-6 border-y border-[#C20066]" aria-hidden="true">
        <div className="flex whitespace-nowrap gap-12 animate-[marquee_28s_linear_infinite] font-display text-3xl md:text-4xl">
          {Array.from({ length: 2 }).map((_, dup) => (
            <div key={dup} className="flex gap-12 shrink-0">
              {["mamy!kongres", "22 maja 2027", "Warszawa", "Bilety Early Bird", "Dla mam z całej Polski", "Inspirujące kobiety", "300+ uczestniczek"].map((t, i) => (
                <span key={`${dup}-${i}`} className="flex items-center gap-12">
                  <span>{t}</span>
                  <span className="text-white/60">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* SPEAKERS TEASER */}
      <section className="bg-[#F4EFE6] py-24" data-testid="speakers-teaser">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-4">Prelegentki 2027</div>
              <h2 className="font-display text-4xl md:text-5xl">Kobiety, które <span className="italic text-[#E6007A]">wiedzą</span>.</h2>
            </div>
            <Link to="/prelegentki" className="text-[#E6007A] font-semibold link-underline inline-flex items-center gap-2">
              Zobacz wszystkie <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="rounded-3xl bg-white/60 border border-[#E8E1D9] p-12 text-center">
            <div className="font-display text-3xl md:text-4xl text-[#2B2623] max-w-2xl mx-auto leading-tight">
              Na dobry skład warto chwilę poczekać...
            </div>
            <p className="text-[#5C534D] mt-4">Skład prelegentek ogłosimy wkrótce.</p>
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="cta-band">
        <div className="rounded-[48px] bg-[#E6007A] text-white p-12 md:p-16 relative overflow-hidden">
          <div className="blob bg-white/20 w-80 h-80 -top-20 -right-20" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-4xl md:text-5xl leading-tight mb-4">
                Do zobaczenia<br />w maju w Warszawie.
              </h3>
              <p className="text-white/80 max-w-md">
                Bilety Early Bird od 22 lutego do 22 marca 2027 lub do wyczerpania puli. Nie zostawiaj tego na później.
              </p>
            </div>
            <div className="md:justify-self-end">
              <Link
                to="/zapisz-sie"
                data-testid="cta-band-register"
                className="btn-lift inline-flex items-center gap-2 rounded-full bg-white text-[#E6007A] px-8 py-4 font-semibold"
              >
                Zapisuję się <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-24" data-testid="faq-teaser">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E6007A] mb-4 text-center">FAQ</div>
        <h2 className="font-display text-4xl md:text-5xl text-center mb-12">Najczęstsze pytania</h2>
        <div className="space-y-4">
          {FAQ.slice(0, 3).map((f, i) => (
            <details key={i} className="rounded-2xl bg-white border border-[#E8E1D9] p-6 group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer font-semibold text-lg flex justify-between items-center">
                {f.q}
                <span className="text-[#E6007A] text-2xl group-open:rotate-45 transition-transform inline-block">+</span>
              </summary>
              <p className="text-[#5C534D] mt-4 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/kontakt" className="text-[#E6007A] font-semibold link-underline">Wszystkie pytania</Link>
        </div>
      </section>
    </div>
  );
}
